/**
 * Daily MITs Service
 *
 * Pure business logic for Most Important Tasks operations.
 * No React or Firebase dependencies.
 */

import { DailyMIT } from '@/types';

// ============================================
// Constants
// ============================================

const DEFAULT_MAX_MITS = 3;

// ============================================
// MIT Sorting
// ============================================

/**
 * Sort MITs by their order field.
 *
 * @param mits - Array of MITs to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortMitsByOrder(mits: DailyMIT[]): DailyMIT[] {
  return [...mits].sort((a, b) => a.order - b.order);
}

// ============================================
// MIT Counting
// ============================================

/**
 * Get the count of completed MITs.
 *
 * @param mits - Array of MITs
 * @returns Number of completed MITs
 */
export function getCompletedMitsCount(mits: DailyMIT[]): number {
  return mits.filter((mit) => mit.completed).length;
}

/**
 * Get the count of incomplete MITs.
 *
 * @param mits - Array of MITs
 * @returns Number of incomplete MITs
 */
export function getIncompleteMitsCount(mits: DailyMIT[]): number {
  return mits.filter((mit) => !mit.completed).length;
}

// ============================================
// MIT Validation
// ============================================

/**
 * Check if a new MIT can be added.
 *
 * @param currentCount - Current number of MITs
 * @param maxMits - Maximum allowed MITs (defaults to 3)
 * @returns True if a new MIT can be added
 */
export function canAddMit(currentCount: number, maxMits: number = DEFAULT_MAX_MITS): boolean {
  return currentCount < maxMits;
}

// ============================================
// MIT ID Generation
// ============================================

/**
 * Generate a unique MIT ID.
 *
 * @returns Unique MIT ID string
 */
export function generateMitId(): string {
  return `mit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================
// MIT Progress
// ============================================

/**
 * Calculate MIT completion percentage.
 *
 * @param mits - Array of MITs
 * @returns Completion percentage (0-100)
 */
export function getMitCompletionPercentage(mits: DailyMIT[]): number {
  if (mits.length === 0) return 0;
  return Math.round((getCompletedMitsCount(mits) / mits.length) * 100);
}

/**
 * Check if all MITs are completed.
 *
 * @param mits - Array of MITs
 * @returns True if all MITs are completed (or array is empty)
 */
export function areAllMitsCompleted(mits: DailyMIT[]): boolean {
  if (mits.length === 0) return true;
  return mits.every((mit) => mit.completed);
}
